<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <div class="absolute inset-0 flex flex-col overflow-y-hidden">
      <div class="flex-1 overflow-y-auto">
        <header class="space-y-1 py-6 px-4 bg-cyan-700">
          <div class="flex items-center justify-between">
            <h2 class="text-lg leading-7 font-medium text-white">
              จัดการฟีเจอร์ลูกค้า
            </h2>
            <button
              @click="onClose"
              aria-label="Close panel"
              class="
                text-cyan-200
                hover:text-white
                transition
                ease-in-out
                duration-150
              "
            >
              <svg
                class="h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>
          </div>
          <div>
            <p class="text-sm leading-5 text-cyan-300">
              จัดการฟีเจอร์ลูกค้า ปิด/เปิดฟีเจอร์ลูกค้าและตั้งค่าการทำงานต่างๆ
            </p>
          </div>
        </header>

        <!-- Error Message -->
        <div
          v-if="errors.length > 0"
          class="px-4 py-3 bg-red-600 bg-opacity-20 flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 text-red-600"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          <p class="text-base font-semibold text-red-800 flex-1 px-3">
            {{ errors[0] }}
          </p>
          <button @click="errors = []" type="button" class="focus:outline-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-red-600"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
        <!-- End Error Message -->

        <div class="p-4 space-y-6">
          <div>
            <AppInput
              v-model="customerFeature.title_th"
              label="ฟีเจอร์"
              type="text"
              disabled
              className="cursor-not-allowed"
            />
          </div>

          <div>
            <AppToggleInput v-model="customerFeature.is_active" />
          </div>

          <div>
            <ValidationProvider
              name="จำกัดจำนวนผู้ใช้สูงสุด"
              rules="required|integer"
              v-slot="{ errors }"
            >
              <AppInput
                v-model.trim="
                  customerFeature.customer_feature.limit_max_employee
                "
                label="จำกัดจำนวนผู้ใช้สูงสุด (0 คือ ไม่จำกัดจำนวน)"
                type="text"
                :disabled="!customerFeature.is_active"
                :className="{
                  'cursor-not-allowed': !customerFeature.is_active,
                }"
              />
              <span
                v-if="customerFeature.is_active"
                class="text-base text-red-600"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>
            <span
              v-if="!customerFeature.is_active"
              class="text-base text-red-600"
              >จำเป็นต้องเปิดฟีเจอร์</span
            >
          </div>

          <div>
            <ValidationProvider
              name="จำนวนผู้ใช้งานฟรี"
              rules="required|integer"
              v-slot="{ errors }"
            >
              <AppInput
                v-model.trim="
                  customerFeature.customer_feature.limit_free_employee
                "
                label="จำนวนผู้ใช้งานฟรี"
                type="text"
                :disabled="!customerFeature.is_active"
                :className="{
                  'cursor-not-allowed': !customerFeature.is_active,
                }"
              />
              <span
                v-if="customerFeature.is_active"
                class="text-base text-red-600"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>
            <span
              v-if="!customerFeature.is_active"
              class="text-base text-red-600"
              >จำเป็นต้องเปิดฟีเจอร์</span
            >
            <span
              v-if="validateLimitFreeEmployee && customerFeature.is_active"
              class="text-base text-red-600"
              >จำนวนผู้ใช้งานฟรี ต้องน้อยกว่าหรือเท่ากับ จำนวนผู้ใช้สูงสุด</span
            >
          </div>

          <div>
            <ValidationProvider
              name="ราคา"
              rules="required|integer"
              v-slot="{ errors }"
            >
              <AppInput
                v-model.trim="customerFeature.customer_feature.price_special"
                label="ราคา"
                type="text"
                :disabled="!customerFeature.is_active"
                :className="{
                  'cursor-not-allowed': !customerFeature.is_active,
                }"
              />
              <span
                v-if="customerFeature.is_active"
                class="text-base text-red-600"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>
            <span
              v-if="!customerFeature.is_active"
              class="text-base text-red-600"
              >จำเป็นต้องเปิดฟีเจอร์</span
            >
          </div>

          <div>
            <ValidationProvider
              name="วันเวลาเริ่มต้นใช้งาน"
              rules="required"
              v-slot="{ errors }"
            >
              <DateTimeInput
                :disabled="!customerFeature.is_active"
                v-model="customerFeature.start_time"
                label="วันเวลาเริ่มต้นใช้งาน"
                @invalid="(value) => (startTimeValid = value)"
              />
              <span
                v-if="customerFeature.is_active"
                class="text-base text-red-600"
                >{{ errors[0] }}</span
              >
            </ValidationProvider>
            <span
              v-if="!customerFeature.is_active"
              class="text-base text-red-600"
              >จำเป็นต้องเปิดฟีเจอร์</span
            >
          </div>

          <div>
            <DateTimeInput
              :disabled="!customerFeature.is_active"
              v-model="customerFeature.end_time"
              label="วันเวลาสิ้นสุดการใช้งาน"
              @invalid="(value) => (endTimeValid = value)"
            />
            <span
              v-if="!customerFeature.is_active"
              class="text-base text-red-600"
              >จำเป็นต้องเปิดฟีเจอร์</span
            >
          </div>

          <div class="sr-only">{{ isDataChange }}</div>
        </div>
      </div>

      <div>
        <div class="p-4 border-t flex justify-end">
          <div class="space-x-4">
            <button
              @click="onCancel"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-gray-300
                shadow-sm
                px-4
                py-2
                bg-white
                text-base
                font-medium
                text-gray-700
                hover:bg-gray-50
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:ml-3 sm:w-auto
              "
            >
              ยกเลิก
            </button>

            <button
              @click="handleSubmit(onSave)"
              :disabled="loading"
              :class="{ 'cursor-not-allowed': loading }"
              type="button"
              class="
                w-full
                inline-flex
                justify-center
                rounded-md
                border border-transparent
                shadow-sm
                px-4
                py-2
                bg-cyan-600
                text-base
                font-medium
                text-white
                hover:bg-cyan-700
                focus:outline-none
                focus:ring-2
                focus:ring-offset-2
                focus:ring-cyan-500
                sm:w-auto
              "
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              บันทึก
            </button>
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<script>
import moment from 'moment'

import AppInput from '@/components/AppInput'
import AppToggleInput from '@/components/AppToggleInput'
import DateTimeInput from '@/components/DateTimeInput'

import { extend } from 'vee-validate'
import { required, integer } from 'vee-validate/dist/rules'

// No message specified.
// extend('required', required)

// Override the default message.
extend('required', {
  ...required,
  message: 'จำเป็นต้องระบุ {_field_}',
})
extend('integer', {
  ...integer,
  message: '{_field_} ต้องเป็นตัวเลขจำนวนเต็ม',
})

export default {
  name: 'FormCustomerFeature',

  props: {
    currentCustomerFeature: {
      type: Object,
    },
    customerFeature: {
      type: Object,
    },
  },

  components: {
    AppInput,
    AppToggleInput,
    DateTimeInput,
  },

  data() {
    return {
      loading: false,
      errors: [],
      startTimeValid: false,
      endTimeValid: false,
    }
  },

  computed: {
    isDataChange() {
      if (
        this.currentCustomerFeature.is_active !==
          this.customerFeature.is_active ||
        this.currentCustomerFeature.customer_feature?.limit_max_employee !=
          this.customerFeature.customer_feature?.limit_max_employee ||
        this.currentCustomerFeature.customer_feature?.limit_free_employee !=
          this.customerFeature.customer_feature?.limit_free_employee ||
        this.currentCustomerFeature.customer_feature?.price_special !=
          this.customerFeature.customer_feature?.price_special ||
        this.currentCustomerFeature.start_time !==
          this.customerFeature.start_time ||
        this.currentCustomerFeature.end_time !== this.customerFeature.end_time
      ) {
        this.$emit('isDataChange', true)
        return true
      }
      this.$emit('isDataChange', false)
      return false
    },

    validateLimitFreeEmployee() {
      if (
        this.customerFeature.customer_feature.limit_free_employee >
          this.customerFeature.customer_feature.limit_max_employee &&
        Number(this.customerFeature.customer_feature.limit_max_employee) !== 0
      ) {
        return true
      }
      return false
    },
  },

  methods: {
    onCancel() {
      this.$emit('onCancel')
    },

    onClose() {
      this.$emit('onClose')
    },

    async onSave() {
      if (this.startTimeValid || this.endTimeValid) {
        return
      }

      if (this.customerFeature.is_active === false) {
        this.deleteCustomerFeature()
        return
      }

      if (
        this.customerFeature.is_active === true &&
        this.currentCustomerFeature.is_active === false
      ) {
        this.addCustomerFeature()
        return
      }

      if (
        this.customerFeature.is_active === true &&
        this.currentCustomerFeature.is_active === true
      ) {
        this.updateCustomerFeature()
        return
      }
    },

    async deleteCustomerFeature() {
      try {
        this.loading = true

        const uuid =
          this.customerFeature.customer_feature.company_feature_toggle_uuid

        if (uuid) {
          await this.$store.dispatch('customer/deleteCustomerFeature', uuid)

          const notification = {
            type: 'success',
            message: 'Your customer feature has been updated!',
          }
          this.$store.dispatch('notification/add', notification)

          this.$emit('isDataChange', false)
          this.onClose()
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(error.response.data.message)
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async addCustomerFeature() {
      if (this.validateLimitFreeEmployee) {
        return
      }

      try {
        this.loading = true

        const uuid = this.$route.params.uuid
        const feature_uuid = this.customerFeature.feature_uuid

        const newCustomerFeature = {
          company_uuid: uuid,
          feature_uuid,
          limit_max_employee: Number(
            this.customerFeature.customer_feature.limit_max_employee
          ),
          limit_free_employee: Number(
            this.customerFeature.customer_feature.limit_free_employee
          ),
          price_special: Number(
            this.customerFeature.customer_feature.price_special
          ),
          start_time: this.customerFeature.start_time
            ? moment
                .utc(this.customerFeature.start_time, 'DD/MM/YYYY HH:mm')
                .format()
            : null,
          end_time: this.customerFeature.end_time
            ? moment
                .utc(this.customerFeature.end_time, 'DD/MM/YYYY HH:mm')
                .format()
            : null,
        }

        await this.$store.dispatch(
          'customer/addCustomerFeature',
          newCustomerFeature
        )

        const notification = {
          type: 'success',
          message: 'Your customer feature has been updated!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(error.response.data.message)
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },

    async updateCustomerFeature() {
      if (this.validateLimitFreeEmployee) {
        return
      }

      try {
        this.loading = true

        const uuid = this.$route.params.uuid
        const feature_uuid = this.customerFeature.feature_uuid

        const customerFeatureUpdate = {
          company_feature_toggle_uuid:
            this.customerFeature.customer_feature.company_feature_toggle_uuid,
          company_uuid: uuid,
          feature_uuid,
          limit_max_employee: Number(
            this.customerFeature.customer_feature.limit_max_employee
          ),
          limit_free_employee: Number(
            this.customerFeature.customer_feature.limit_free_employee
          ),
          price_special: Number(
            this.customerFeature.customer_feature.price_special
          ),
          start_time: this.customerFeature.start_time
            ? moment
                .utc(this.customerFeature.start_time, 'DD/MM/YYYY HH:mm')
                .format()
            : null,
          end_time: this.customerFeature.end_time
            ? moment
                .utc(this.customerFeature.end_time, 'DD/MM/YYYY HH:mm')
                .format()
            : null,
        }

        await this.$store.dispatch(
          'customer/editCustomerFeature',
          customerFeatureUpdate
        )

        const notification = {
          type: 'success',
          message: 'Your customer feature has been updated!',
        }
        this.$store.dispatch('notification/add', notification)

        this.$emit('isDataChange', false)
        this.onClose()
      } catch (error) {
        if (error.response && error.response.data) {
          this.errors.push(error.response.data.message)
        }

        console.log(error.response)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
